import { Injectable } from '@angular/core';
import { take, tap } from 'rxjs';

import { DprActivityGridData } from '@dpr-details/models/grid';
import {
  DprActivitiesDurationHistory,
  DprActivity,
  DprDetails,
} from '@shared/models/dpr';
import { OfflineFacade } from '@shared/state/facades';
import { computeDiffTimeAsMinutes, convertTimeToDate } from '@shared/utils';
import cloneDeep from 'lodash.clonedeep';

@Injectable()
export class DprActivityHistoryService {
  constructor(private offlineFacade: OfflineFacade) {}

  updateFutureDprActivitiesHistories(
    gridsData: Map<number, DprActivityGridData[]>,
    currentDprDetail: DprDetails
  ): void {
    this.offlineFacade.query.combinedDprDetailsList$
      .pipe(
        take(1),
        tap((combinedDprDetails) => {
          const dprDetailsToUpdate = combinedDprDetails
            ?.filter(
              (x) =>
                new Date(x.dprDateTime).getTime() >
                new Date(currentDprDetail.dprDateTime).getTime()
            )
            .sort(
              (x, y) =>
                new Date(x.dprDateTime).getTime() -
                new Date(y.dprDateTime).getTime()
            );

          if (!dprDetailsToUpdate?.length) {
            return;
          }

          let updatedHistory = this.computeUpdatedActivitiesHistory(
            gridsData,
            currentDprDetail
          );
          let history = cloneDeep(updatedHistory);

          for (const dprDetailToUpdate of dprDetailsToUpdate) {
            updatedHistory = this.computeActivityHistory(
              [
                ...dprDetailToUpdate.dprActivities,
                ...dprDetailToUpdate.dprParallelActivities,
              ],
              updatedHistory
            );

            this.offlineFacade.setDprActivityHistory(
              dprDetailToUpdate.id,
              history
            );

            history = cloneDeep(updatedHistory);
          }
        })
      )
      .subscribe();
  }

  private computeActivityHistory(
    dprActivities: DprActivity[],
    histories: DprActivitiesDurationHistory[]
  ): DprActivitiesDurationHistory[] {
    const activityHistory = [];
    for (const history of histories) {
      let totalDuration = 0;
      dprActivities
        .filter(
          (activity) =>
            activity.activityGroupId === history.activityGroupId &&
            activity.activityId === history.activityId
        )
        .forEach(
          (activity) =>
            (totalDuration += computeDiffTimeAsMinutes(
              convertTimeToDate(activity.finish),
              convertTimeToDate(activity.start)
            ))
        );

      activityHistory.push({
        ...history,
        totalDuration: history.totalDuration + totalDuration,
      });
    }

    return activityHistory;
  }

  private computeUpdatedActivitiesHistory(
    gridsData: Map<number, DprActivityGridData[]>,
    currentDprDetail: DprDetails
  ): DprActivitiesDurationHistory[] {
    const currentDurations: DprActivitiesDurationHistory[] = [];

    // add all the duration of the current DPR activities
    for (const activityStreamId of gridsData.keys()) {
      const dprActivityData = gridsData.get(activityStreamId);

      for (const data of dprActivityData) {
        const currentValue = currentDurations.find(
          (x) =>
            x.parallelActivityType === (data.parallelActivityType ?? 0) &&
            x.activityGroupId === data.activityGroupId &&
            x.activityId === data.activityId
        );

        if (currentValue) {
          currentValue.totalDuration += computeDiffTimeAsMinutes(
            convertTimeToDate(data.finish),
            convertTimeToDate(data.start)
          );
        } else {
          currentDurations.push({
            totalDuration: computeDiffTimeAsMinutes(
              convertTimeToDate(data.finish),
              convertTimeToDate(data.start)
            ),
            parallelActivityType: data.parallelActivityType ?? 0,
            activityGroupId: data.activityGroupId,
            activityId: data.activityId,
          });
        }
      }
    }

    // add the previous durations with the current ones and reduce the resulting array
    //  by (parallelActivityType, activityGroupId, activityId)
    const reducedCombinedUpdatedHistory: DprActivitiesDurationHistory[] =
      Array.from(
        // combine the previous history with the newly added values
        [
          ...currentDprDetail.dprActivitiesDurationHistory,
          ...currentDurations,
        ].reduce((acc, cur) => {
          // reduce the total duration values by common parallelActivityType_ActivityGroupId_ActivityId
          const combinedId = `${cur.parallelActivityType}_${cur.activityGroupId}_${cur.activityId}`;
          acc.set(combinedId, (acc.get(combinedId) ?? 0) + cur.totalDuration);
          return acc;
        }, new Map<string, number>()),
        // transform the resulting map in an array by extracting the ids from the combine id and the duration value
        ([combinedId, total]) => {
          const [parallelActivityType, activityGroupId, activityId] = combinedId
            .split('_')
            .map((x) => +x);
          return {
            parallelActivityType,
            activityGroupId,
            activityId,
            totalDuration: total,
          };
        }
      );

    return reducedCombinedUpdatedHistory;
  }
}
